import React, { useState } from 'react';
import { apiClient } from '../../api/client';
import { DocumentDetail } from './ContentViewer';

interface DocumentEditorProps {
  document: DocumentDetail | null;
  moduleId: string | null;
  onSave: () => void;
  onCancel: () => void;
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '2rem',
    height: '100%',
    overflowY: 'auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem',
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: '#1a6b3c',
    margin: 0,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.3rem',
  },
  label: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: '#555',
  },
  input: {
    padding: '0.5rem 0.75rem',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    fontSize: '0.9rem',
    outline: 'none',
  },
  textarea: {
    padding: '0.75rem',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    fontSize: '0.85rem',
    fontFamily: 'monospace',
    resize: 'vertical',
    minHeight: '300px',
    outline: 'none',
    lineHeight: 1.6,
  },
  checkboxRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  checkbox: {
    width: '16px',
    height: '16px',
    cursor: 'pointer',
  },
  checkboxLabel: {
    fontSize: '0.85rem',
    color: '#555',
    cursor: 'pointer',
  },
  actions: {
    display: 'flex',
    gap: '0.75rem',
    marginTop: '0.5rem',
  },
  saveBtn: {
    padding: '0.5rem 1.25rem',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: 500,
  },
  cancelBtn: {
    padding: '0.5rem 1.25rem',
    backgroundColor: '#fff',
    color: '#666',
    border: '1px solid #ccc',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.85rem',
  },
  previewBtn: {
    padding: '0.5rem 1rem',
    backgroundColor: '#c9a84c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: 500,
  },
  previewArea: {
    padding: '1rem',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    backgroundColor: '#fafafa',
    whiteSpace: 'pre-wrap',
    lineHeight: 1.7,
    fontSize: '0.9rem',
    color: '#333',
    minHeight: '200px',
    wordBreak: 'break-word',
  },
  error: {
    color: '#d32f2f',
    fontSize: '0.8rem',
  },
};

export function DocumentEditor({ document, moduleId, onSave, onCancel }: DocumentEditorProps) {
  const [title, setTitle] = useState(document?.title || '');
  const [body, setBody] = useState(document?.body || '');
  const [externalUrl, setExternalUrl] = useState(document?.externalUrl || '');
  const [isExternal, setIsExternal] = useState(document?.isExternal || false);
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    if (isExternal && !externalUrl.trim()) {
      setError('External URL is required for external documents');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (document) {
        // Edit existing
        await apiClient(`/documents/${document.id}`, {
          method: 'PATCH',
          body: {
            title: title.trim(),
            body: isExternal ? null : body,
            externalUrl: isExternal ? externalUrl.trim() : null,
            isExternal,
          },
        });
      } else {
        // Create new
        await apiClient('/documents', {
          method: 'POST',
          body: {
            title: title.trim(),
            body: isExternal ? null : body,
            externalUrl: isExternal ? externalUrl.trim() : null,
            isExternal,
            moduleId,
          },
        });
      }
      onSave();
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'message' in err
        ? (err as { message: string }).message
        : 'Failed to save document';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>
          {document ? 'Edit Document' : 'New Document'}
        </h2>
        <button
          style={styles.previewBtn}
          onClick={() => setShowPreview(!showPreview)}
        >
          {showPreview ? '✏️ Edit' : '👁️ Preview'}
        </button>
      </div>

      {showPreview ? (
        <div>
          <h3 style={{ color: '#1a6b3c', marginBottom: '0.5rem' }}>{title || 'Untitled'}</h3>
          <div style={styles.previewArea}>{body}</div>
        </div>
      ) : (
        <div style={styles.form}>
          <div style={styles.fieldGroup}>
            <label style={styles.label}>Title</label>
            <input
              style={styles.input}
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Document title"
            />
          </div>

          <div style={styles.checkboxRow}>
            <input
              style={styles.checkbox}
              type="checkbox"
              id="isExternal"
              checked={isExternal}
              onChange={(e) => setIsExternal(e.target.checked)}
            />
            <label style={styles.checkboxLabel} htmlFor="isExternal">
              External document (link to URL)
            </label>
          </div>

          {isExternal ? (
            <div style={styles.fieldGroup}>
              <label style={styles.label}>External URL</label>
              <input
                style={styles.input}
                type="text"
                value={externalUrl}
                onChange={(e) => setExternalUrl(e.target.value)}
                placeholder="https://example.com/doc.html or /path/to/file.html"
              />
            </div>
          ) : (
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Body (Markdown)</label>
              <textarea
                style={styles.textarea}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Write your document content here..."
              />
            </div>
          )}

          {error && <div style={styles.error}>{error}</div>}

          <div style={styles.actions}>
            <button style={styles.saveBtn} onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : '💾 Save'}
            </button>
            <button style={styles.cancelBtn} onClick={onCancel}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
